import { monthLabelFromIndex, quarterIndexFromTimestamp, quarterLabelPt } from "../../../dashboard-framework/aggregateRows";
import { paletteColorAt } from "../../../dashboard-framework/colors";
import { ChartSeriesConfig } from "../../../dashboard-framework/types";
import { attendanceRatePct } from "../shared/sharedMetrics";
import { ACTIVE_USER_STATUS } from "./teamKpisConfig";

// Lógica pura do team-kpis (réplica do Zoho "Info for KPIs service related" — ver spec.md).
// Atribuição PROVADA por SQL: gestoras por treatment.client_owner, psicólogas por sessions.technician;
// satisfação com dedup por (survey, pessoa) porque a junção treatments_technicians_1 tem duplicados.

export type AttendanceItem = {
    monthIndex: number;
    personKey: string;
    personName: string;
    status: string;
    count: number;
    personActive: boolean;
};

export const normalizePersonName = (firstName: string | null | undefined, lastName: string | null | undefined) => {
    const composed = [firstName, lastName].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    return composed;
};

const monthIndexOf = (year: number, month: number) => year * 12 + (month - 1);

const monthIndexFromIsoDate = (isoDate: string) => {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    const index = monthIndexOf(year, month);
    return index;
};

type OwnerSessionItem = {
    id?: number | string;
    status: string;
    start_date: string | null;
    treatment?: {
        id?: number | string;
        client?: { client_id?: string | null } | null;
        treatment_status_new?: string | null;
        date_of_start?: string | null;
        client_owner?: {
            id?: number | string;
            user_id?: { id: string; first_name: string | null; last_name: string | null; status?: string | null } | null;
        } | null;
        theurapists_involved_in_treamtent?: Array<{ technicians_id: number | string | null }> | null;
    } | null;
};

// Owner que também é psicóloga do tratamento não conta como gestora: esse tratamento é tratado
// na secção das psicólogas (comparação por id de técnica, client_owner vs junção de terapeutas).
// A junção pode vir com o id escalar (fetch das gestoras) ou expandida até ao utilizador (fetch dos ativos).
export type OwnerTherapistCheckTreatment = {
    client_owner?: { id?: number | string } | null;
    theurapists_involved_in_treamtent?: Array<{ technicians_id: number | string | { id?: number | string } | null }> | null;
} | null | undefined;

const junctionTechnicianIdOf = (technicianRef: number | string | { id?: number | string } | null) => {
    const technicianId = technicianRef !== null && typeof technicianRef === "object" ? technicianRef.id : technicianRef;
    return technicianId;
};

export const ownerIsTherapistOfTreatment = (treatment: OwnerTherapistCheckTreatment) => {
    const ownerTechnicianId = treatment?.client_owner?.id;
    if (ownerTechnicianId === null || ownerTechnicianId === undefined) return false;
    const therapistIds = (treatment?.theurapists_involved_in_treamtent || []).map((junction) => String(junctionTechnicianIdOf(junction.technicians_id)));
    const isTherapist = therapistIds.includes(String(ownerTechnicianId));
    return isTherapist;
};

// Regra do compromisso: tratamentos em que o cliente nunca compareceu (0 realizadas no universo
// do período) saem de TODOS os números — sem primeira presença, o profissional não teve
// oportunidade de criar compromisso. Sessão sem tratamento é avaliada isoladamente.
const commitmentKeyOf = (sessionItem: { id?: number | string; treatment?: { id?: number | string } | null }) => {
    const treatmentId = sessionItem.treatment?.id;
    const key = treatmentId === null || treatmentId === undefined ? `session:${sessionItem.id}` : `treatment:${treatmentId}`;
    return key;
};

const excludeClientsWhoNeverShowedUp = <SessionType extends { id?: number | string; status: string; treatment?: { id?: number | string } | null }>(
    sessionItems: Array<SessionType>
) => {
    const keysWithDoneSession = new Set(sessionItems.filter((sessionItem) => sessionItem.status === "done").map(commitmentKeyOf));
    const survivors = sessionItems.filter((sessionItem) => keysWithDoneSession.has(commitmentKeyOf(sessionItem)));
    return survivors;
};

// Predicado ÚNICO de elegibilidade + filtro opcional por pessoas/mês. Alimenta os itens do widget
// E as linhas do drill-down — por construção a modal mostra exatamente as sessões contadas.
// A regra do compromisso corre sobre o universo COMPLETO do período (antes do filtro pessoa/mês):
// um cliente que faltou em jan mas compareceu em fev mantém a falta de jan.
export const ownerSessionsMatching = (sessionItems: Array<OwnerSessionItem>, personKeys: Set<string> | null, monthIndex: number | null) => {
    const eligibleSessions = sessionItems.filter((sessionItem) => {
        const ownerUser = sessionItem.treatment?.client_owner?.user_id;
        if (!ownerUser || !sessionItem.start_date) return false;
        const isEligible = !ownerIsTherapistOfTreatment(sessionItem.treatment);
        return isEligible;
    });
    const matching = excludeClientsWhoNeverShowedUp(eligibleSessions).filter((sessionItem) => {
        if (personKeys !== null && !personKeys.has(sessionItem.treatment!.client_owner!.user_id!.id)) return false;
        if (monthIndex !== null && monthIndexFromIsoDate(sessionItem.start_date!) !== monthIndex) return false;
        return true;
    });
    return matching;
};

// Linhas das modais de drill: 1 linha por (tratamento, pessoa) por trás do valor clicado, com
// breakdown dos estados — a soma da coluna "sessions" reconcilia com o widget. Sessão sem
// tratamento (existe em Psicologia) vira linha com id null (sem rota para a ficha).
export type TreatmentDrillRow = {
    id: number | string | null;
    clientNumber: string | null;
    personName: string;
    statusNew: string | null;
    dateOfStart: string | null;
    sessions: number;
    done: number;
    missed: number;
    cancelled: number;
    ratePct: number | null;
};

type TreatmentDrillEntry = {
    status: string;
    personName: string;
    treatment: OwnerSessionItem["treatment"];
};

const treatmentDrillRowsFromEntries = (entries: Array<TreatmentDrillEntry>) => {
    const rowsByTreatmentAndPerson = new Map<string, TreatmentDrillRow>();
    entries.forEach((entry) => {
        const treatmentId = entry.treatment?.id ?? null;
        const rowKey = `${treatmentId ?? "none"}|${entry.personName}`;
        const existingRow = rowsByTreatmentAndPerson.get(rowKey);
        const row = existingRow || {
            id: treatmentId,
            clientNumber: entry.treatment?.client?.client_id ?? null,
            personName: entry.personName,
            statusNew: entry.treatment?.treatment_status_new ?? null,
            dateOfStart: entry.treatment?.date_of_start ?? null,
            sessions: 0,
            done: 0,
            missed: 0,
            cancelled: 0,
            ratePct: null,
        };
        row.sessions += 1;
        if (entry.status === "done") row.done += 1;
        if (entry.status === "missed") row.missed += 1;
        if (entry.status === "cancelled") row.cancelled += 1;
        rowsByTreatmentAndPerson.set(rowKey, row);
    });

    const rows = Array.from(rowsByTreatmentAndPerson.values()).map((row) => ({
        ...row,
        ratePct: attendanceRatePct(row.done, row.cancelled, row.missed),
    }));
    return rows;
};

export const ownerTreatmentRowsFromSessions = (
    sessionItems: Array<OwnerSessionItem>,
    personKeys: Set<string> | null,
    monthIndex: number | null
) => {
    const entries = ownerSessionsMatching(sessionItems, personKeys, monthIndex).map((sessionItem) => {
        const ownerUser = sessionItem.treatment!.client_owner!.user_id!;
        const entry: TreatmentDrillEntry = {
            status: sessionItem.status,
            personName: normalizePersonName(ownerUser.first_name, ownerUser.last_name),
            treatment: sessionItem.treatment,
        };
        return entry;
    });
    const rows = treatmentDrillRowsFromEntries(entries);
    return rows;
};

// Psicólogas: atribuição por quem DEU a sessão (sessions.technician), não pelo tratamento —
// o mesmo tratamento pode ter sessões de várias psicólogas, cada uma com a sua linha.
export const NO_TECHNICIAN_PERSON_KEY = "none";
export const NO_TECHNICIAN_PERSON_NAME = "Sem técnica associada";

type PsychologistSessionItem = {
    id?: number | string;
    status: string;
    start_date: string | null;
    technician?: {
        id?: number | string;
        user_id?: { first_name: string | null; last_name: string | null; status?: string | null } | null;
    } | null;
    treatment?: OwnerSessionItem["treatment"];
};

const psychologistPersonKeyOf = (sessionItem: PsychologistSessionItem) => {
    const technicianId = sessionItem.technician?.id;
    const personKey = technicianId === null || technicianId === undefined ? NO_TECHNICIAN_PERSON_KEY : String(technicianId);
    return personKey;
};

// Mesma estrutura do ownerSessionsMatching: regra do compromisso sobre o universo do período,
// depois filtro por pessoas/mês.
export const psychologistSessionsMatching = (
    sessionItems: Array<PsychologistSessionItem>,
    personKeys: Set<string> | null,
    monthIndex: number | null
) => {
    const eligibleSessions = sessionItems.filter((sessionItem) => Boolean(sessionItem.start_date));
    const matching = excludeClientsWhoNeverShowedUp(eligibleSessions).filter((sessionItem) => {
        if (personKeys !== null && !personKeys.has(psychologistPersonKeyOf(sessionItem))) return false;
        if (monthIndex !== null && monthIndexFromIsoDate(sessionItem.start_date!) !== monthIndex) return false;
        return true;
    });
    return matching;
};

const psychologistPersonNameOf = (sessionItem: PsychologistSessionItem) => {
    const personName =
        normalizePersonName(sessionItem.technician?.user_id?.first_name, sessionItem.technician?.user_id?.last_name) || NO_TECHNICIAN_PERSON_NAME;
    return personName;
};

// Itens do widget (vista da diretora) a partir do fetch raw — mesmo universo/regras do drill.
export const attendanceItemsFromPsychologistSessionItems = (sessionItems: Array<PsychologistSessionItem>) => {
    const items = psychologistSessionsMatching(sessionItems, null, null).map((sessionItem) => {
        const item: AttendanceItem = {
            monthIndex: monthIndexFromIsoDate(sessionItem.start_date!),
            personKey: psychologistPersonKeyOf(sessionItem),
            personName: psychologistPersonNameOf(sessionItem),
            status: sessionItem.status,
            count: 1,
            personActive: sessionItem.technician ? sessionItem.technician.user_id?.status === ACTIVE_USER_STATUS : true,
        };
        return item;
    });
    return items;
};

export const psychologistTreatmentRowsFromSessions = (
    sessionItems: Array<PsychologistSessionItem>,
    personKeys: Set<string> | null,
    monthIndex: number | null
) => {
    const entries = psychologistSessionsMatching(sessionItems, personKeys, monthIndex).map((sessionItem) => {
        const entry: TreatmentDrillEntry = {
            status: sessionItem.status,
            personName: psychologistPersonNameOf(sessionItem),
            treatment: sessionItem.treatment,
        };
        return entry;
    });
    const rows = treatmentDrillRowsFromEntries(entries);
    return rows;
};

export const attendanceItemsFromOwnerSessionItems = (sessionItems: Array<OwnerSessionItem>) => {
    const items = ownerSessionsMatching(sessionItems, null, null).map((sessionItem) => {
        const ownerUser = sessionItem.treatment!.client_owner!.user_id!;
        const item: AttendanceItem = {
            monthIndex: monthIndexFromIsoDate(sessionItem.start_date!),
            personKey: ownerUser.id,
            personName: normalizePersonName(ownerUser.first_name, ownerUser.last_name),
            status: sessionItem.status,
            count: 1,
            personActive: ownerUser.status === ACTIVE_USER_STATUS,
        };
        return item;
    });
    return items;
};

type StatusTotals = {
    done: number;
    cancelled: number;
    missed: number;
    total: number;
};

const statusTotalsOf = (items: Array<AttendanceItem>) => {
    const totals: StatusTotals = { done: 0, cancelled: 0, missed: 0, total: 0 };
    items.forEach((item) => {
        totals.total += item.count;
        if (item.status === "done") totals.done += item.count;
        if (item.status === "cancelled") totals.cancelled += item.count;
        if (item.status === "missed") totals.missed += item.count;
    });
    return totals;
};

export const attendanceRateOfItems = (items: Array<AttendanceItem>) => {
    const totals = statusTotalsOf(items);
    const ratePct = attendanceRatePct(totals.done, totals.cancelled, totals.missed);
    return ratePct;
};

export type MonthlyAttendanceRow = {
    monthIndex: number;
    monthLabel: string;
    sessions: number;
    ratePct: number | null;
};

export const monthlyAttendanceTableRows = (items: Array<AttendanceItem>) => {
    const itemsByMonth = new Map<number, Array<AttendanceItem>>();
    items.forEach((item) => {
        const monthItems = itemsByMonth.get(item.monthIndex) || [];
        monthItems.push(item);
        itemsByMonth.set(item.monthIndex, monthItems);
    });

    const rows: Array<MonthlyAttendanceRow> = Array.from(itemsByMonth.entries())
        .sort(([firstMonth], [secondMonth]) => firstMonth - secondMonth)
        .map(([monthIndex, monthItems]) => {
            const totals = statusTotalsOf(monthItems);
            const row: MonthlyAttendanceRow = {
                monthIndex,
                monthLabel: monthLabelFromIndex(monthIndex),
                sessions: totals.total,
                ratePct: attendanceRatePct(totals.done, totals.cancelled, totals.missed),
            };
            return row;
        });
    return rows;
};

export type PersonAttendanceBar = {
    personKey: string;
    personName: string;
    sessions: number;
    ratePct: number | null;
};

export const personAttendanceBars = (items: Array<AttendanceItem>, excludedPersonNames: Array<string> = []) => {
    const itemsByPerson = new Map<string, Array<AttendanceItem>>();
    items.forEach((item) => {
        if (excludedPersonNames.includes(item.personName)) return;
        const personItems = itemsByPerson.get(item.personKey) || [];
        personItems.push(item);
        itemsByPerson.set(item.personKey, personItems);
    });

    const bars: Array<PersonAttendanceBar> = Array.from(itemsByPerson.values())
        .map((personItems) => {
            const totals = statusTotalsOf(personItems);
            const bar: PersonAttendanceBar = {
                personKey: personItems[0].personKey,
                personName: personItems[0].personName,
                sessions: totals.total,
                ratePct: attendanceRatePct(totals.done, totals.cancelled, totals.missed),
            };
            return bar;
        })
        .sort((firstBar, secondBar) => firstBar.personName.localeCompare(secondBar.personName));
    return bars;
};

export type SurveyPersonItem = {
    surveyId: number;
    wouldRecommend: number | null;
    personKey: string;
    personName: string;
    personActive: boolean;
};

type SurveyUser = { id: string; first_name: string | null; last_name: string | null; status: string | null } | null | undefined;

type SurveyItem = {
    id: number;
    would_recommend_clinic: number | null;
    date_sent?: string | null;
    moment?: string | null;
    other_comments?: string | null;
    treatment?: {
        client_owner?: { user_id?: SurveyUser } | null;
        theurapists_involved_in_treamtent?: Array<{ technicians_id?: { user_id?: SurveyUser } | null } | null> | null;
    } | null;
};

const surveyPersonItemOf = (survey: SurveyItem, user: SurveyUser) => {
    if (!user) return null;
    const item: SurveyPersonItem = {
        surveyId: survey.id,
        wouldRecommend: survey.would_recommend_clinic,
        personKey: user.id,
        personName: normalizePersonName(user.first_name, user.last_name),
        personActive: user.status === "active",
    };
    return item;
};

// Owner que também é psicóloga do tratamento não conta na atribuição por gestora — o questionário
// é tratado só na atribuição das psicólogas (mesma regra da comparência; comparação por user id,
// os fields da satisfação não trazem ids de técnica).
const surveyOwnerIsTherapist = (survey: SurveyItem) => {
    const ownerUserId = survey.treatment?.client_owner?.user_id?.id;
    if (ownerUserId === null || ownerUserId === undefined) return false;
    const therapistUserIds = (survey.treatment?.theurapists_involved_in_treamtent || []).map(
        (junctionRow) => junctionRow?.technicians_id?.user_id?.id
    );
    const isTherapist = therapistUserIds.includes(ownerUserId);
    return isTherapist;
};

const personItemsOfSurvey = (survey: SurveyItem, attribution: "psychologists" | "owner") => {
    const items: Array<SurveyPersonItem> = [];
    if (attribution === "owner") {
        if (surveyOwnerIsTherapist(survey)) return items;
        const ownerItem = surveyPersonItemOf(survey, survey.treatment?.client_owner?.user_id);
        if (ownerItem) items.push(ownerItem);
        return items;
    }
    (survey.treatment?.theurapists_involved_in_treamtent || []).forEach((junctionRow) => {
        const personItem = surveyPersonItemOf(survey, junctionRow?.technicians_id?.user_id);
        if (personItem) items.push(personItem);
    });
    return items;
};

export const surveyPersonItemsFromSurveys = (surveys: Array<SurveyItem>, attribution: "psychologists" | "owner") => {
    const items: Array<SurveyPersonItem> = [];
    surveys.forEach((survey) => {
        personItemsOfSurvey(survey, attribution).forEach((personItem) => items.push(personItem));
    });
    return items;
};

// Linhas do drill das tabelas de satisfação: os questionários (distintos) de UMA pessoa na
// atribuição dada — mesma extração das tabelas, por isso a contagem bate por construção
// (inclui a exclusão owner-é-psicóloga na atribuição por gestora).
export type SurveyDrillRow = {
    id: number;
    dateSent: string | null;
    wouldRecommend: number | null;
    moment: string | null;
    comments: string | null;
};

export const personSurveyDrillRows = (surveys: Array<SurveyItem>, attribution: "psychologists" | "owner", personKey: string) => {
    const rowsBySurveyId = new Map<number, SurveyDrillRow>();
    surveys.forEach((survey) => {
        const personMatches = personItemsOfSurvey(survey, attribution).some((personItem) => personItem.personKey === personKey);
        if (!personMatches) return;
        rowsBySurveyId.set(survey.id, {
            id: survey.id,
            dateSent: survey.date_sent ?? null,
            wouldRecommend: survey.would_recommend_clinic,
            moment: survey.moment ?? null,
            comments: survey.other_comments ?? null,
        });
    });
    const rows = Array.from(rowsBySurveyId.values());
    return rows;
};

export type SatisfactionRow = {
    personKey: string;
    personName: string;
    surveys: number;
    avg: number | null;
};

const averageOf = (values: Array<number>) => {
    if (values.length === 0) return null;
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;
    return average;
};

export const satisfactionTableRows = (items: Array<SurveyPersonItem>, includeSuspended = false) => {
    const dedupedByPersonAndSurvey = new Map<string, SurveyPersonItem>();
    items.forEach((item) => {
        if (!includeSuspended && !item.personActive) return;
        dedupedByPersonAndSurvey.set(`${item.personKey}|${item.surveyId}`, item);
    });
    const dedupedItems = Array.from(dedupedByPersonAndSurvey.values());

    const itemsByPerson = new Map<string, Array<SurveyPersonItem>>();
    dedupedItems.forEach((item) => {
        const personItems = itemsByPerson.get(item.personKey) || [];
        personItems.push(item);
        itemsByPerson.set(item.personKey, personItems);
    });

    const rows: Array<SatisfactionRow> = Array.from(itemsByPerson.values())
        .map((personItems) => {
            const grades = personItems
                .map((item) => item.wouldRecommend)
                .filter((grade): grade is number => grade !== null && grade !== undefined);
            const row: SatisfactionRow = {
                personKey: personItems[0].personKey,
                personName: personItems[0].personName,
                surveys: personItems.length,
                avg: averageOf(grades),
            };
            return row;
        })
        .sort((firstRow, secondRow) => (secondRow.avg ?? -1) - (firstRow.avg ?? -1) || firstRow.personName.localeCompare(secondRow.personName));

    const distinctSurveys = new Map<number, SurveyPersonItem>();
    dedupedItems.forEach((item) => {
        distinctSurveys.set(item.surveyId, item);
    });
    const totalGrades = Array.from(distinctSurveys.values())
        .map((item) => item.wouldRecommend)
        .filter((grade): grade is number => grade !== null && grade !== undefined);
    const totals = { surveys: distinctSurveys.size, avg: averageOf(totalGrades) };

    return { rows, totals };
};

// Série temporal de satisfação por trimestre. Reutiliza a extração/dedup da tabela e:
// - restringe ao próprio ANTES de agrupar (vista individual não revela colegas da mesma junção);
// - linha de equipa = média POOLED por survey distinto no trimestre (igual ao total da tabela);
// - trimestre sem dados para uma pessoa fica a undefined (gap na linha, nunca 0).
export type SurveyQuarterItem = SurveyPersonItem & { quarterIndex: number };

export const TREND_COUNT_SUFFIX = "__n";
const SATISFACTION_TEAM_SERIES_KEY = "__team";
const TREND_AGGREGATE_COLOR = "#1A1A1A";
// Linha agregada (Total clínica / Média da equipa) mais grossa para se destacar das linhas individuais.
const AGGREGATE_STROKE_WIDTH = 4;

export const surveyQuarterItemsFromSurveys = (surveys: Array<SurveyItem>, attribution: "psychologists" | "owner") => {
    const items: Array<SurveyQuarterItem> = [];
    surveys.forEach((survey) => {
        if (!survey.date_sent) return;
        const quarterIndex = quarterIndexFromTimestamp(survey.date_sent);
        personItemsOfSurvey(survey, attribution).forEach((personItem) => {
            items.push({ ...personItem, quarterIndex });
        });
    });
    return items;
};

export const satisfactionQuarterChart = (
    quarterItems: Array<SurveyQuarterItem>,
    quarterIndexes: Array<number>,
    restrictToPersonKey: string | null,
    includeTeamAverage: boolean,
    excludedPersonNames: Array<string> = [],
    includeSuspended = false
) => {
    // Âmbito segue o filtro "Utilizadores": só ativos (defeito) ou ativos+suspensos. Exclui sempre contas de dev.
    // Linhas e média da equipa usam o MESMO conjunto, por isso a média bate sempre com as linhas mostradas.
    const eligibleItems = quarterItems.filter(
        (item) => !excludedPersonNames.includes(item.personName) && (includeSuspended || item.personActive)
    );
    const scopedItems = restrictToPersonKey ? eligibleItems.filter((item) => item.personKey === restrictToPersonKey) : eligibleItems;

    const dedupedByPersonAndSurvey = new Map<string, SurveyQuarterItem>();
    scopedItems.forEach((item) => dedupedByPersonAndSurvey.set(`${item.personKey}|${item.surveyId}`, item));
    const dedupedLineItems = Array.from(dedupedByPersonAndSurvey.values());

    const personNameByKey = new Map<string, string>();
    const itemsByPersonQuarter = new Map<string, Array<SurveyQuarterItem>>();
    dedupedLineItems.forEach((item) => {
        personNameByKey.set(item.personKey, item.personName);
        const personQuarterKey = `${item.personKey}|${item.quarterIndex}`;
        const personQuarterItems = itemsByPersonQuarter.get(personQuarterKey) || [];
        personQuarterItems.push(item);
        itemsByPersonQuarter.set(personQuarterKey, personQuarterItems);
    });

    // Questionários distintos por trimestre para a média da equipa (mesmo âmbito das linhas).
    const distinctSurveysByQuarter = new Map<number, Map<number, SurveyQuarterItem>>();
    eligibleItems.forEach((item) => {
        const surveysOfQuarter = distinctSurveysByQuarter.get(item.quarterIndex) || new Map<number, SurveyQuarterItem>();
        surveysOfQuarter.set(item.surveyId, item);
        distinctSurveysByQuarter.set(item.quarterIndex, surveysOfQuarter);
    });

    const personKeysSorted = Array.from(personNameByKey.keys()).sort((firstKey, secondKey) =>
        (personNameByKey.get(firstKey) || "").localeCompare(personNameByKey.get(secondKey) || "")
    );

    const points = quarterIndexes.map((quarterIndex) => {
        const point: Record<string, string | number> = { monthLabel: quarterLabelPt(quarterIndex) };

        personKeysSorted.forEach((personKey) => {
            const personQuarterItems = itemsByPersonQuarter.get(`${personKey}|${quarterIndex}`) || [];
            if (personQuarterItems.length === 0) return;
            const grades = personQuarterItems
                .map((item) => item.wouldRecommend)
                .filter((grade): grade is number => grade !== null && grade !== undefined);
            const average = averageOf(grades);
            if (average === null) return;
            point[personKey] = average;
            point[`${personKey}${TREND_COUNT_SUFFIX}`] = personQuarterItems.length;
        });

        if (includeTeamAverage) {
            const surveysOfQuarter = distinctSurveysByQuarter.get(quarterIndex);
            const teamGrades = surveysOfQuarter
                ? Array.from(surveysOfQuarter.values())
                      .map((item) => item.wouldRecommend)
                      .filter((grade): grade is number => grade !== null && grade !== undefined)
                : [];
            const teamAverage = averageOf(teamGrades);
            if (teamAverage !== null && surveysOfQuarter) {
                point[SATISFACTION_TEAM_SERIES_KEY] = teamAverage;
                point[`${SATISFACTION_TEAM_SERIES_KEY}${TREND_COUNT_SUFFIX}`] = surveysOfQuarter.size;
            }
        }

        return point;
    });

    const personSeries: Array<ChartSeriesConfig> = personKeysSorted.map((personKey, index) => {
        const serie: ChartSeriesConfig = { key: personKey, label: personNameByKey.get(personKey) || personKey, color: paletteColorAt(index) };
        return serie;
    });
    const teamSeries: Array<ChartSeriesConfig> = includeTeamAverage
        ? [{ key: SATISFACTION_TEAM_SERIES_KEY, label: "Média da equipa", color: TREND_AGGREGATE_COLOR, strokeWidth: AGGREGATE_STROKE_WIDTH }]
        : [];

    const result = { points, series: [...personSeries, ...teamSeries] };
    return result;
};
